import React from 'react';
import { AlignCenterIcon, AlignLeftIcon, AlignRightIcon, BoldIcon, CheckSquareIcon, Heading1Icon, Heading2Icon, Heading3Icon, ItalicIcon, LinkIcon, ListIcon, ListOrderedIcon, MinusIcon, QuoteIcon, StrikethroughIcon, TableIcon, TypeIcon, UnderlineIcon } from 'lucide-react';
import type { BlockKind } from '../../types/notes';
interface EditorToolbarProps {
  activeKind: BlockKind | null;
  activeLevel?: 1 | 2 | 3;
  onInline: (command: 'bold' | 'italic' | 'underline' | 'strikeThrough' | 'createLink') => void;
  onKind: (kind: BlockKind, level?: 1 | 2 | 3) => void;
  onAlign: (align: 'left' | 'center' | 'right') => void;
  onInsert: (kind: 'separator' | 'table') => void;
  disabled?: boolean;
}
function ToolButton({
  label,
  active,
  disabled,
  onClick,
  children
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" title={label} aria-label={label} aria-pressed={active} disabled={disabled}
  // Keep the caret in the editable block when a control is pressed.
  onMouseDown={event => event.preventDefault()} onClick={onClick} className={`flex h-8 w-8 items-center justify-center rounded-md border transition-colors duration-150 ease-out disabled:opacity-40 disabled:pointer-events-none ${active ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-transparent text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-3540" data-mp="j8a9yhl1pbpa">
      {children}
    </button>;
}
const Divider = () => <span className="mx-1 h-5 w-px shrink-0 bg-line" aria-hidden="true" data-id="element-3541" data-mp="3ch5e1ipw4kg2" />;
export function EditorToolbar({
  activeKind,
  activeLevel,
  onInline,
  onKind,
  onAlign,
  onInsert,
  disabled
}: EditorToolbarProps) {
  return <div role="toolbar" aria-label="Formatting" className="flex flex-wrap items-center gap-0.5 border-b border-line bg-surface px-3 py-1.5" data-id="element-3542" data-mp="rqmwqgx9gvb1">
      <ToolButton label="Bold (Ctrl+B)" disabled={disabled} onClick={() => onInline('bold')} data-id="element-3543" data-mp="2ssmb9apdfmaz">
        <BoldIcon className="h-4 w-4" data-id="element-3544" data-mp="2nv40dersmx0c" />
      </ToolButton>
      <ToolButton label="Italic (Ctrl+I)" disabled={disabled} onClick={() => onInline('italic')} data-id="element-3545" data-mp="2nwnht8p6re3p">
        <ItalicIcon className="h-4 w-4" data-id="element-3546" data-mp="31rpwak83mbs4" />
      </ToolButton>
      <ToolButton label="Underline (Ctrl+U)" disabled={disabled} onClick={() => onInline('underline')} data-id="element-3547" data-mp="2wwakdxtnipyc">
        <UnderlineIcon className="h-4 w-4" data-id="element-3548" data-mp="2967wltwrs65j" />
      </ToolButton>
      <ToolButton label="Strikethrough" disabled={disabled} onClick={() => onInline('strikeThrough')} data-id="element-3549" data-mp="254cw2rmnxwst">
        <StrikethroughIcon className="h-4 w-4" data-id="element-3550" data-mp="2ivrhf541ma31" />
      </ToolButton>
      <ToolButton label="Link" disabled={disabled} onClick={() => onInline('createLink')} data-id="element-3551" data-mp="2ix2k11lr5bsk">
        <LinkIcon className="h-4 w-4" data-id="element-3552" data-mp="2e2esetvgc19r" />
      </ToolButton>

      <Divider data-id="element-3553" data-mp="1qdvkwgk87siq" />

      <ToolButton label="Body text" disabled={disabled} active={activeKind === 'paragraph'} onClick={() => onKind('paragraph')} data-id="element-3554" data-mp="1qdcd3soaikif">
        <TypeIcon className="h-4 w-4" data-id="element-3555" data-mp="xjfxv9x7p1v" />
      </ToolButton>
      <ToolButton label="Heading 1" disabled={disabled} active={activeKind === 'heading' && activeLevel === 1} onClick={() => onKind('heading', 1)} data-id="element-3556" data-mp="vyle5nlw640">
        <Heading1Icon className="h-4 w-4" data-id="element-3557" data-mp="3o0ejytyy8nfz" />
      </ToolButton>
      <ToolButton label="Heading 2" disabled={disabled} active={activeKind === 'heading' && activeLevel === 2} onClick={() => onKind('heading', 2)} data-id="element-3558" data-mp="3t2nbdlw6bhfz">
        <Heading2Icon className="h-4 w-4" data-id="element-3559" data-mp="onnidzbdhira" />
      </ToolButton>
      <ToolButton label="Heading 3" disabled={disabled} active={activeKind === 'heading' && activeLevel === 3} onClick={() => onKind('heading', 3)} data-id="element-3560" data-mp="anl7ix1hfplj">
        <Heading3Icon className="h-4 w-4" data-id="element-3561" data-mp="foio556ugbkx" />
      </ToolButton>

      <Divider data-id="element-3562" data-mp="2vd28beubu01g" />

      <ToolButton label="Bulleted list" disabled={disabled} active={activeKind === 'bulleted'} onClick={() => onKind('bulleted')} data-id="element-3563" data-mp="2vciplvwur7md">
        <ListIcon className="h-4 w-4" data-id="element-3564" data-mp="3fnlga1ay793f" />
      </ToolButton>
      <ToolButton label="Numbered list" disabled={disabled} active={activeKind === 'numbered'} onClick={() => onKind('numbered')} data-id="element-3565" data-mp="3avv5ju83mjbh">
        <ListOrderedIcon className="h-4 w-4" data-id="element-3566" data-mp="36uhw7a5srxzt" />
      </ToolButton>
      <ToolButton label="Checklist" disabled={disabled} active={activeKind === 'checklist'} onClick={() => onKind('checklist')} data-id="element-3567" data-mp="31w81t1rwd1f9">
        <CheckSquareIcon className="h-4 w-4" data-id="element-3568" data-mp="3uk3uzwi9cb7a" />
      </ToolButton>
      <ToolButton label="Quote" disabled={disabled} active={activeKind === 'quote'} onClick={() => onKind('quote')} data-id="element-3569" data-mp="3ukm2jpadebui">
        <QuoteIcon className="h-4 w-4" data-id="element-3570" data-mp="3kmfxwzx6irly" />
      </ToolButton>

      <Divider data-id="element-3571" data-mp="3ko0x52ufuyq5" />

      <ToolButton label="Align left" disabled={disabled} onClick={() => onAlign('left')} data-id="element-3572" data-mp="2e5b1ynddlukd">
        <AlignLeftIcon className="h-4 w-4" data-id="element-3573" data-mp="296hnosq9lzj5" />
      </ToolButton>
      <ToolButton label="Align centre" disabled={disabled} onClick={() => onAlign('center')} data-id="element-3574" data-mp="2956fmgpse5m8">
        <AlignCenterIcon className="h-4 w-4" data-id="element-3575" data-mp="1z442wxssp1qx" />
      </ToolButton>
      <ToolButton label="Align right" disabled={disabled} onClick={() => onAlign('right')} data-id="element-3576" data-mp="240umhv3t6fh3">
        <AlignRightIcon className="h-4 w-4" data-id="element-3577" data-mp="29079qgyldok7" />
      </ToolButton>

      <Divider data-id="element-3578" data-mp="28yw0w2qahqm8" />

      <ToolButton label="Separator" disabled={disabled} onClick={() => onInsert('separator')} data-id="element-3579" data-mp="2dr73xegbtsj0">
        <MinusIcon className="h-4 w-4" data-id="element-3580" data-mp="2htarv2k6mpl6" />
      </ToolButton>
      <ToolButton label="Table" disabled={disabled} onClick={() => onInsert('table')} data-id="element-3581" data-mp="2hssmngfjl114">
        <TableIcon className="h-4 w-4" data-id="element-3582" data-mp="2mqkds88d4j49" />
      </ToolButton>
    </div>;
}